import { FetchAPI, Mutex, ParamsSerializer } from './auth-service';

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

class SensorsDataService {
    constructor () {
        if (!SensorsDataService.instance) {
            SensorsDataService.instance = this;
            this.namesMux = new Mutex();
        }

        this.getData = (searchParams, cmcs) => {
            const p = { target_cmc: cmcs };
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/' : ''}sensors/?${searchParams}&${ParamsSerializer(p)}`,
                method: 'GET',
            }).then(res => MC2CHECK ? res : res.objects);
        };

        this.getSensor = (uuid, cmc) => {
            return FetchAPI({
                url: MC2CHECK ? `mc2/sensors/${cmc}/${uuid}/` : `sensors/${uuid}/`,
                method: 'GET',
            });
        };

        this.addSensor = (data) => {
            return FetchAPI({
                url: 'sensors/',
                method: 'POST',
                body: data,
            });
        };

        this.updateSensor = (uuid, data) => {
            return FetchAPI({
                url: `sensors/${uuid}`,
                method: 'PUT',
                body: data,
            });
        };

        this.deleteSensor = (uuid) => {
            return FetchAPI({
                url: `sensors/${uuid}`,
                method: 'DELETE',
            });
        };

        this.getSensorHealthHistory = (uuid, cmc) => {
            return FetchAPI({
                url: `${MC2CHECK ? 'mc2/' : ''}sensors/${MC2CHECK ? cmc + '/' : ''}${uuid}/health/history`,
                method: 'GET'
            });
        };

        this.deleteSensorHealthHistory = (uuid) => {
            return FetchAPI({
                url: `sensors/${uuid}/health/`,
                method: 'DELETE'
            });
        };

        this.getSensorBTStatus = (uuid) => {
            return FetchAPI({
                url: `sensors/${uuid}/health/btstatus`,
                method: 'GET'
            });
        };

        this.getLogDump = (host, token, rules) => {
            return FetchAPI({
                url: `sensors/${host}/logdump/?t=${token}&rules=${rules}`,
                method: 'GET',
            });
        };

        this.getSensorLogs = (uuid) => {
            return FetchAPI({
                url: `sensors/${uuid}/log/history/`,
                method: 'GET'
            }).then((res) => {
                if (Object.prototype.toString.call(res).includes('Array')) {
                    return res;
                }
                return [];
            });
        };

        this.getMetadataFeature = (uuid) => {
            return FetchAPI({
                url: `sensors/${uuid}/feature/send_metadata_to_the_cmc.please`,
                method: 'GET',
            }).then(res => {
                if (!res) { return { available: false, enabled: false } }
                return res;
            });
        };

        this.getPCAPFeature = (uuid) => {
            return FetchAPI({
                url: `sensors/${uuid}/feature/full_packet_capture.please`,
                method: 'GET',
            }).then(res => {
                if (!res) { return { available: false, enabled: false } }
                return res;
            });
        };

        this.getTCPFeature = (uuid) => {
            return FetchAPI({
                url: `sensors/${uuid}/feature/advanced_tcp_processing.please`,
                method: 'GET',
            }).then(res => {
                if (!res) { return { available: false, enabled: false } }
                return res;
            });
        };

        this.getLocalSNMP = (uuid) => {
            return FetchAPI({
                url: `sensors/${uuid}/local_snmp`,
                method: 'GET',
            }).then(res => {
                if (!res) { return { syscontact: '', syslocation: '' } }
                return res;
            });
        };

        this.updateMetadataFeature = (uuid, data) => {
            return FetchAPI({
                url: `sensors/${uuid}/feature/send_metadata_to_the_cmc.please`,
                method: 'PUT',
                body: data,
            });
        };

        this.updatePCAPFeature = (uuid, data) => {
            return FetchAPI({
                url: `sensors/${uuid}/feature/full_packet_capture.please`,
                method: 'PUT',
                body: data,
            });
        };

        this.updateTCPFeature = (uuid, data) => {
            return FetchAPI({
                url: `sensors/${uuid}/feature/advanced_tcp_processing.please`,
                method: 'PUT',
                body: data,
            });
        };

        this.updateLocalSNMP = (uuid, data) => {
            return FetchAPI({
                url: `sensors/${uuid}/local_snmp`,
                method: 'PUT',
                body: data,
            });
        };

        this.getPcapStats = (uuid, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                url: `${MC2CHECK ? `mc2/cmcapi/${cmc.name}/` : ''}sensors/${uuid}/pcap_stats`,
                method: 'GET',
            });
        };

        this.getApps = (uuid, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                url: `${MC2CHECK ? `mc2/cmcapi/${cmc.name}/` : ''}sensors/apps/${uuid}`,
                method: 'GET',
            });
        };

        this.getSavedSearches = () => {
            try {
                let searches = localStorage.getItem('savedSearches_sensors') || '[]';
                if (searches.length < 1) searches = '[]';
                return JSON.parse(searches);
            } catch (err) {
                console.log('getSavedSearches error', err);
                return [];
            }
        };

        this.saveSearch = (str) => {
            if (!str || !str.length) return;
            const searches = this.getSavedSearches();
            const i = searches.findIndex(s => s === str);
            if (i >= 0) searches.splice(i, 1);
            searches.unshift(str);
            searches.splice(5);
            localStorage.setItem('savedSearches_sensors', JSON.stringify(searches));
        };

        this.reregisterSensor = (host) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'PUT',
                url: `sensors/${host}/reregister`,
            });
        };

        return SensorsDataService.instance;
    }

    // temporary - search terms will be pushed to API
    filter (search) {
        return this.getTitles();
    }

    // only want to call this guy one at a time
    async getNames (refresh, cmc) {
        const unlock = await this.namesMux.lock();
        if (!refresh && this.names) {
            unlock();
            return Promise.resolve(this.names);
        }

        const p = { target_cmc: cmc };

        return FetchAPI({
            method: 'GET',
            url: `${MC2CHECK ? 'mc2/' : ''}sensornames/?${ParamsSerializer(p)}`,
            cache: !refresh
        }).then(res => {
            if (cmc) {
                // just replace the ones on this cmc
                this.namesm = { ...(this.namesm || {}), [cmc]: res };
                this.names = Object.values(this.namesm).reduce((a, c) => {
                    return { ...a, ...c };
                });
            } else {
                this.names = res;
            }
            return this.names;
        }).finally(() => unlock());
    }
}

const SensorsService = new SensorsDataService();

export {
    SensorsService as default,
};
